import { useCallback, useEffect, useState } from 'react';

import { selectActiveUserId } from 'features/applications/applicationsSelectors';
import {
  selectActiveJobseeker,
  selectJobseekers
} from 'features/jobseekers/jobseekersSelectors';
import jobseekersService from 'features/jobseekers/jobseekersService';
import {
  addJobseeker,
  updateJobseeker
} from 'features/jobseekers/jobseekersSlice';
import { closeSidebar } from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { useTranslation } from 'react-i18next';
import { Jobseeker } from 'types/jobseekers';

const useGetJobseeker = (): {
  jobseeker: Jobseeker | undefined;
  isLoading: boolean;
  fetchJobseekerData: (jobseekerId: string) => Promise<void>;
} => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch();
  const catchError = useErrorCatch();

  const jobseekerId = useAppSelector(selectActiveUserId);
  const jobseekers = useAppSelector(selectJobseekers);
  const jobseeker = useAppSelector(selectActiveJobseeker);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchJobseekerData = useCallback(
    async (jobseekerId: string): Promise<void> => {
      try {
        setIsLoading(true);
        const res = await jobseekersService.getJobseekerById(jobseekerId);
        if (!jobseekers.find((jobseeker) => jobseeker.id === res.id)) {
          dispatch(addJobseeker(res));
        } else {
          dispatch(updateJobseeker(res));
        }
      } catch (error) {
        catchError({
          error,
          default: {
            message: t('error.message.operationFailed'),
            description: t('error.message.fetchJobseeker')
          }
        });
        dispatch(closeSidebar());
      } finally {
        setIsLoading(false);
      }
    },
    [catchError, dispatch]
  );

  useEffect(() => {
    if (!jobseekerId) {
      return;
    }
    fetchJobseekerData(jobseekerId);
  }, [jobseekerId]);

  return { jobseeker, isLoading, fetchJobseekerData };
};

export default useGetJobseeker;
